/**
 * React Query hooks for BTX benefit plan groups management
 *
 * Provides hooks for querying benefit plan groups.
 * Uses React Query for caching, loading states, and automatic refetching.
 */

import {
  useQuery,
  UseQueryResult,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosInstance } from "axios";

import { BenefitPlanGroup } from "../types/models";
import {
  listBenefitPlanGroups as apiListBenefitPlanGroups,
  getBenefitPlanGroup as apiGetBenefitPlanGroup,
  getBenefitPlanGroupPremiums as apiGetBenefitPlanGroupPremiums,
  ListBenefitPlanGroupsParams,
  BenefitPlanGroupPremiums,
} from "../api/benefit-plan-groups";

/**
 * Query key factory for benefit plan groups
 */
export const benefitPlanGroupQueryKeys = {
  all: ["benefitPlanGroups"] as const,
  lists: (rfpId: string) =>
    [...benefitPlanGroupQueryKeys.all, rfpId, "list"] as const,
  list: (params: ListBenefitPlanGroupsParams) =>
    [
      ...benefitPlanGroupQueryKeys.lists(params.rfpId),
      params.coverageType,
      params.productType,
    ] as const,
  details: () => [...benefitPlanGroupQueryKeys.all, "detail"] as const,
  detail: (id: string) => [...benefitPlanGroupQueryKeys.details(), id] as const,
  premiums: () => [...benefitPlanGroupQueryKeys.all, "premiums"] as const,
  premium: (id: string) =>
    [...benefitPlanGroupQueryKeys.premiums(), id] as const,
};

/**
 * Hook to list benefit plan groups for an RFP and coverage type
 *
 * @param params - Query parameters (rfpId, coverageType, productType optional)
 * @param axiosInstance - Axios instance for making API calls
 * @param options - React Query options
 * @returns Query result with list of benefit plan groups
 *
 * @example
 * ```tsx
 * function BenefitPlanGroupsList({ rfpId, coverageType }: { rfpId: string; coverageType: string }) {
 *   const axiosInstance = useApi();
 *   const { data, isLoading, error } = useListBenefitPlanGroups(
 *     { rfpId, coverageType },
 *     axiosInstance
 *   );
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <Error message={error.message} />;
 *
 *   return (
 *     <ul>
 *       {data?.map(group => (
 *         <li key={group.id}>{group.nickname || `Group ${group.position}`}</li>
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */
export const useListBenefitPlanGroups = (
  params: ListBenefitPlanGroupsParams,
  axiosInstance: AxiosInstance,
  options?: Omit<
    UseQueryOptions<BenefitPlanGroup[], Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<BenefitPlanGroup[], Error> =>
  useQuery({
    queryKey: benefitPlanGroupQueryKeys.list(params),
    queryFn: () => apiListBenefitPlanGroups(params, axiosInstance),
    enabled: !!params.rfpId && !!params.coverageType && !!axiosInstance,
    ...options,
  });

/**
 * Hook to get a single benefit plan group by ID
 *
 * @param groupId - Benefit plan group ID
 * @param axiosInstance - Axios instance for making API calls
 * @param options - React Query options
 * @returns Query result with benefit plan group data
 *
 * @example
 * ```tsx
 * function BenefitPlanGroupDetails({ groupId }: { groupId: string }) {
 *   const axiosInstance = useApi();
 *   const { data: group, isLoading } = useGetBenefitPlanGroup(groupId, axiosInstance);
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       <h2>{group?.nickname}</h2>
 *       <p>Plans: {group?.benefitPlans.length}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export const useGetBenefitPlanGroup = (
  groupId: string,
  axiosInstance: AxiosInstance,
  options?: Omit<
    UseQueryOptions<BenefitPlanGroup, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<BenefitPlanGroup, Error> =>
  useQuery({
    queryKey: benefitPlanGroupQueryKeys.detail(groupId),
    queryFn: () => apiGetBenefitPlanGroup(groupId, axiosInstance),
    enabled: !!groupId && !!axiosInstance,
    ...options,
  });

/**
 * Hook to get only a benefit plan group's premium rollups.
 *
 * Used by the rates summary to refresh backend-computed premiums after an
 * inline rate edit without refetching the full group (which would remount the
 * editable rates grid). Invalidate `benefitPlanGroupQueryKeys.premiums()` on
 * save to trigger a refresh.
 *
 * @param groupId - Benefit plan group ID
 * @param axiosInstance - Axios instance for making API calls
 * @param options - React Query options
 * @returns Query result with the group's premium rollups
 */
export const useGetBenefitPlanGroupPremiums = (
  groupId: string,
  axiosInstance: AxiosInstance,
  options?: Omit<
    UseQueryOptions<BenefitPlanGroupPremiums, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<BenefitPlanGroupPremiums, Error> =>
  useQuery({
    queryKey: benefitPlanGroupQueryKeys.premium(groupId),
    queryFn: () => apiGetBenefitPlanGroupPremiums(groupId, axiosInstance),
    enabled: !!groupId && !!axiosInstance,
    ...options,
  });
